import { Consumer } from 'src/domain/consumer';
import {
    OrganizationGroup,
    OrganizationHierarchyLevel,
} from 'src/domain/organization';

const getGroupLabel = (group: OrganizationGroup) => {
    return group.name?.trim().length ? group.name : group.id;
};

const getNormalizedGroupIdSet = (groupIds?: string[]) => {
    const normalizedGroupIds = (groupIds ?? []).filter(Boolean);
    return normalizedGroupIds.length ? new Set(normalizedGroupIds) : undefined;
};

export const getOccupiedRoomIds = (
    consumers: Consumer[],
    options?: { excludeConsumerId?: string },
) => {
    const occupiedRoomIds = new Set<string>();

    consumers.forEach(consumer => {
        if (consumer.id === options?.excludeConsumerId) {
            return;
        }

        (consumer.groupIds ?? []).forEach(groupId => {
            if (groupId) {
                occupiedRoomIds.add(groupId);
            }
        });
    });

    return [...occupiedRoomIds];
};

const getLeafHierarchyLevelIds = (
    organizationHierarchy: OrganizationHierarchyLevel[],
) => {
    const hasChild = new Set<string>();

    organizationHierarchy.forEach(level => {
        if (level.parentId) {
            hasChild.add(level.parentId);
        }
    });

    const leafLevelIds = new Set<string>();

    organizationHierarchy.forEach(level => {
        if (!hasChild.has(level.id)) {
            leafLevelIds.add(level.id);
        }
    });

    return leafLevelIds;
};

export const getAvailableLeafGroups = ({
    organizationGroups,
    organizationHierarchy,
    allowedGroupIds,
    excludedGroupIds,
}: {
    organizationGroups: OrganizationGroup[];
    organizationHierarchy?: OrganizationHierarchyLevel[];
    allowedGroupIds?: string[];
    excludedGroupIds?: string[];
}) => {
    const parentIds = new Set(
        organizationGroups.map(group => group.parentId).filter(Boolean),
    );
    const leafHierarchyLevelIds = organizationHierarchy?.length
        ? getLeafHierarchyLevelIds(organizationHierarchy)
        : undefined;
    const allowedGroupIdSet = getNormalizedGroupIdSet(allowedGroupIds);
    const excludedGroupIdSet = getNormalizedGroupIdSet(excludedGroupIds);

    return organizationGroups
        .filter(group => {
            if (parentIds.has(group.id)) {
                return false;
            }

            if (!leafHierarchyLevelIds) {
                return true;
            }

            return Boolean(
                group.levelId && leafHierarchyLevelIds.has(group.levelId),
            );
        })
        .filter(group =>
            allowedGroupIdSet ? allowedGroupIdSet.has(group.id) : true,
        )
        .filter(group =>
            excludedGroupIdSet ? !excludedGroupIdSet.has(group.id) : true,
        )
        .sort((a, b) =>
            getGroupLabel(a).localeCompare(getGroupLabel(b), undefined, {
                sensitivity: 'base',
                numeric: true,
            }),
        );
};
